import type { LifeSpace } from '../../db/models'
import type { TodoistProject } from './types'

/**
 * **Lebensbereich je Todoist-Projekt** (T06, #171). Todoist kennt keine
 * Lebensbereiche; der Nutzer wählt sie in der Vorschau. Fehlt eine Wahl,
 * gilt Persönlich (Konzept). Die Inbox wählt das „Kurz notiert“, in dem
 * ihre Aufgaben landen.
 */
export type LifeSpaceChoices = Readonly<Record<string, LifeSpace>>

export const DEFAULT_LIFE_SPACE: LifeSpace = 'personal'

export function lifeSpaceFor(choices: LifeSpaceChoices, projectId: string): LifeSpace {
  return choices[projectId] ?? DEFAULT_LIFE_SPACE
}

/**
 * Setzt ein Projekt **und alle seine Nachfahren** — Todoist-Nutzer ordnen meist
 * ganze Äste einem Bereich zu. Ein Unterprojekt lässt sich danach einzeln
 * umstellen; das Elternprojekt bleibt dabei, wie es ist.
 */
export function chooseLifeSpace(
  choices: LifeSpaceChoices,
  projects: readonly TodoistProject[],
  projectId: string,
  value: LifeSpace,
): LifeSpaceChoices {
  const next: Record<string, LifeSpace> = { ...choices, [projectId]: value }
  const seen = new Set([projectId])
  const queue = [projectId]
  while (queue.length > 0) {
    const parent = queue.shift()!
    for (const child of projects) {
      if (child.parent_id !== parent || seen.has(child.id)) continue
      seen.add(child.id)
      next[child.id] = value
      queue.push(child.id)
    }
  }
  return next
}

/** „Alle auf …“: jedes Projekt, die Inbox eingeschlossen. */
export function chooseAll(projects: readonly TodoistProject[], value: LifeSpace): LifeSpaceChoices {
  return Object.fromEntries(projects.map(p => [p.id, value]))
}
